import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import useImageZoom from '../../hooks/useImageZoom';
import './PhotoPreview.css';

const computePreviewLayout = (imageDimensions, anchorPosition) => {
    const margin = 20;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const maxWidth = windowWidth - margin * 2;
    const maxHeight = windowHeight - margin * 2;

    const scaleX = maxWidth / imageDimensions.width;
    const scaleY = maxHeight / imageDimensions.height;
    const scale = Math.min(scaleX, scaleY, 1);

    const baseWidth = imageDimensions.width * scale;
    const baseHeight = imageDimensions.height * scale;

    let left = anchorPosition.x + 20;
    let top = anchorPosition.y - 20;

    if (left + baseWidth + margin > windowWidth) {
        left = anchorPosition.x - baseWidth - 20;
    }
    if (left < margin) {
        left = margin;
    }

    if (top + baseHeight + margin > windowHeight) {
        top = anchorPosition.y - baseHeight - 20;
    }
    if (top < margin) {
        top = margin;
    }

    return { left, top, baseWidth, baseHeight };
};

const PhotoPreviewModal = ({ isVisible, photo, anchorPosition, onPreviewHoverChange }) => {
    const imageRef = useRef(null);
    const viewportRef = useRef(null);
    const transformLayerRef = useRef(null);

    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);

    useEffect(() => {
        if (isVisible && photo?.url) {
            setImageLoaded(false);
            setImageDimensions({ width: 0, height: 0 });

            const img = new Image();
            img.onload = () => {
                setImageDimensions({
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                });
                setImageLoaded(true);
            };
            img.onerror = () => {
                setImageLoaded(true);
            };
            img.src = photo.url;
        } else {
            setImageLoaded(false);
            setImageDimensions({ width: 0, height: 0 });
        }
    }, [isVisible, photo?.url]);

    const layout = useMemo(() => {
        if (!anchorPosition || !imageLoaded || !imageDimensions.width) return null;
        return computePreviewLayout(imageDimensions, anchorPosition);
    }, [anchorPosition, imageLoaded, imageDimensions]);

    const baseSize = layout
        ? { width: layout.baseWidth, height: layout.baseHeight }
        : null;

    const { zoom, zoomPercent } = useImageZoom({
        imageRef,
        viewportRef,
        transformLayerRef,
        resetKey: photo?.url,
        baseSize,
        enabled: isVisible && imageLoaded && !!baseSize,
    });

    if (!isVisible || !photo || !anchorPosition) return null;

    const fallbackLayout = layout || {
        left: Math.max(20, anchorPosition.x + 20),
        top: Math.max(20, anchorPosition.y - 20),
        baseWidth: 320,
        baseHeight: 240,
    };

    const previewContent = (
        <div
            className="photo-preview-modal"
            style={{
                left: `${fallbackLayout.left}px`,
                top: `${fallbackLayout.top}px`,
                width: `${fallbackLayout.baseWidth}px`,
                height: `${fallbackLayout.baseHeight}px`,
            }}
            onMouseEnter={() => onPreviewHoverChange?.(true)}
            onMouseLeave={() => onPreviewHoverChange?.(false)}
        >
            {!layout && (
                <div className="photo-preview-loading" aria-hidden="true">
                    Cargando…
                </div>
            )}
            {layout && (
                <>
                    <div
                        ref={viewportRef}
                        className="photo-preview-viewport image-zoom-viewport"
                    >
                        <div ref={transformLayerRef} className="photo-preview-transform-layer">
                            <img
                                ref={imageRef}
                                src={photo.url}
                                alt={photo.name || 'Preview'}
                                className="photo-preview-modal-image"
                                draggable={false}
                                onError={(e) => {
                                    e.target.src = '/placeholder-image.png';
                                    e.target.alt = 'Error al cargar imagen';
                                }}
                            />
                        </div>
                    </div>
                    {zoom > 1 && (
                        <span className="photo-preview-zoom-badge" aria-live="polite">
                            {zoomPercent}%
                        </span>
                    )}
                </>
            )}
        </div>
    );

    return createPortal(previewContent, document.body);
};

export default PhotoPreviewModal;
